import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useConfig } from '../contexts/RestaurantConfigContext';

export default function CartBar({ itemCount, total, tableNumber, restaurantId, offers = [] }) {
    const navigate = useNavigate();
    const { config } = useConfig();
    const cartConfig = config?.customization?.cart || {};

    // Find nearest threshold offer for savings progress
    const savingsInfo = useMemo(() => {
        if (!offers.length || cartConfig.show_savings_progress === false) return null;
        // Look for flat/percentage offers with a min_order_value threshold above current total
        const thresholdOffers = offers
            .filter(o => o.min_order_value > 0 && o.min_order_value > total)
            .sort((a, b) => a.min_order_value - b.min_order_value);

        if (!thresholdOffers.length) return null;
        const nearest = thresholdOffers[0];
        const remaining = nearest.min_order_value - total;
        const label = nearest.type === 'percentage'
            ? `${nearest.discount_value}%`
            : nearest.type === 'flat'
                ? `₹${nearest.discount_value}`
                : 'free item';
        return { remaining: Math.ceil(remaining), label };
    }, [offers, total, cartConfig.show_savings_progress]);

    // Check if any auto-apply offer is now active (min_order_value met)
    const autoAppliedOffer = useMemo(() => {
        return offers.find(o => o.is_auto_apply && (!o.min_order_value || total >= o.min_order_value));
    }, [offers, total]);

    if (itemCount === 0) return null;

    const handleOpenCart = () => {
        navigate(`/menu/${restaurantId}/cart${tableNumber ? `?table=${tableNumber}` : ''}`);
    };

    const messages = cartConfig.messages || [
        "You're ₹{remaining} away from {offer} OFF 🎉",
        "Coupon auto-applied"
    ];

    const getSavingsMessage = () => {
        if (!savingsInfo) return '';
        return messages[0].replace('{remaining}', savingsInfo.remaining).replace('{offer}', savingsInfo.label);
    };

    return (
        <AnimatePresence>
            <motion.footer
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="fixed bottom-4 left-4 right-4 z-50 pointer-events-none"
            >
                <div className="max-w-[500px] mx-auto pointer-events-auto space-y-2">

                    {/* Savings progress message */}
                    <AnimatePresence>
                        {savingsInfo && (
                            <motion.div
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                className="bg-green-600 text-white text-xs font-bold rounded-2xl px-4 py-2.5 text-center shadow-lg animate-pulse-savings"
                            >
                                {getSavingsMessage()}
                            </motion.div>
                        )}
                        {!savingsInfo && autoAppliedOffer && (
                            <motion.div
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                className="bg-primary text-charcoal text-xs font-bold rounded-2xl px-4 py-2.5 text-center shadow-lg"
                            >
                                {messages[1]}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Main cart bar */}
                    <button type="button"
                        onClick={handleOpenCart}
                        className="bg-charcoal rounded-2xl shadow-xl px-4 py-3.5 flex items-center justify-between cursor-pointer hover:bg-charcoal/90 active:scale-[0.98] transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <span className="material-symbols-outlined text-primary text-xl">shopping_cart</span>
                                <motion.span
                                    key={itemCount}
                                    initial={{ scale: 1.4 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-2 -right-2 bg-primary text-charcoal text-[9px] font-black w-4 h-4 flex items-center justify-center rounded-full"
                                >
                                    {itemCount}
                                </motion.span>
                            </div>
                            <div>
                                <p className="text-white text-sm font-bold leading-none">
                                    {itemCount} Item{itemCount !== 1 ? 's' : ''}
                                </p>
                                <p className="text-white/60 text-[10px] font-medium leading-none mt-0.5">
                                    {tableNumber ? `Table ${tableNumber}` : 'Takeaway'}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-white font-black text-base">₹{total.toFixed(0)}</span>
                            <div className="flex items-center gap-1 bg-primary text-charcoal px-4 py-2 rounded-xl font-black text-xs uppercase tracking-wider">
                                Checkout
                                <span className="material-symbols-outlined text-sm">chevron_right</span>
                            </div>
                        </div>
                    </button>
                </div>
            </motion.footer>
        </AnimatePresence>
    );
}
